﻿// The one chart in the product, and the two rules it has to keep.
//
// Loaded on demand and never from the bundle: app/ is the publisher backend as
// well as the moderation surface, so bundling this would make every venue
// download half a megabyte for a chart only a moderator ever opens.
//
// Served from public/vendor/, like the map libraries — a devDependency copied
// to our own origin by npm run vendor, so the four runtime dependencies stay
// four and nothing in a browser reaches a host we do not run.

import { el } from './dom.js';

let loading = null;

function load() {
  if (globalThis.ApexCharts) return Promise.resolve(globalThis.ApexCharts);
  loading ??= new Promise((resolve, reject) => {
    // Both stylesheets travel with the script, and the id on the first one is
    // load-bearing rather than decoration: ApexCharts injects its own <style>
    // at render time unless it finds `#apexcharts-css` already in the document,
    // the production CSP is style-src 'self' and refuses that <style>, and what
    // is left is an unstyled canvas — a giant accent circle across the page
    // rather than anything a reader would recognise as a missing stylesheet.
    //
    // The legend's rules are in the second file and are injected separately,
    // with no id to check, which is why `injectStyleSheet` below is switched
    // off as well. Linking one file and leaving the other is the shape this bug
    // takes when it looks fixed.
    stylesheet('/assets/vendor/apexcharts.css', 'apexcharts-css');
    stylesheet('/assets/vendor/apexcharts-legend.css');
    // /assets/vendor/, not /vendor/: public/ is mounted at /assets, which is
    // the same address the map libraries load from in server/web/router.js.
    const script = el('script', { src: '/assets/vendor/apexcharts.js' });
    script.addEventListener('load', () => resolve(globalThis.ApexCharts));
    script.addEventListener('error', () => reject(new Error('chart library unavailable')));
    document.head.append(script);
  });
  return loading;
}

// Idempotent by href: load() is memoised, but a moderator who opens the panel,
// navigates away and comes back re-enters through the resolved promise, and two
// links to the same file would accumulate one per visit.
function stylesheet(href, id) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  document.head.append(el('link', { rel: 'stylesheet', href, id }));
}

// The palette is read from the computed tokens, the way public/basemap.js
// builds the map style. A chart library ships its own colours and would put
// them past tokens.css, which is the one place a colour is allowed to live —
// and reading them at render time is what makes the theme toggle restyle the
// chart without a second palette existing anywhere.
// There is no `--border` token — the design system draws separations with the
// surface ramp, which is why the deleted-tokens guard in test/design.test.js
// lists `--border-width` among the values a component must not reach for.
// `--surface-20` is the step the register rules already use.
function palette() {
  const style = getComputedStyle(document.documentElement);
  const token = (name) => style.getPropertyValue(name).trim();
  return {
    accent: token('--accent'),
    text: token('--text-muted'),
    line: token('--surface-20'),
  };
}

// A bar per run, oldest left. `staged` is what the run wrote — deliberately not
// `promotable`, which is a property of the whole queue rather than of one pass
// and would draw the same number for every bar.
export function runsChart(runs, { label }) {
  const host = el('div', { class: 'chart' });
  if (!runs?.length) return host;

  const rows = [...runs].reverse();

  // ApexCharts measures its container at render time and keeps that number.
  // The panel is built into a detached fragment — renderCrawl() fills the
  // outlet before returning it — so rendering straight away produced an SVG
  // `width="0"` that never grew back. Waiting for the first non-zero width is
  // what makes it independent of when the caller attaches us.
  whenSized(host, () => load().then((ApexCharts) => {
    const colours = palette();
    new ApexCharts(host, {
      chart: {
        type: 'bar', height: 180, toolbar: { show: false },
        fontFamily: 'inherit', background: 'transparent',
        animations: { enabled: false },
        // Both stylesheets are linked above, from our own origin. Without this
        // the library appends the legend's rules as a <style> the CSP refuses,
        // which costs a console error on every render and leaves the one rule
        // that sizes the legend marker unapplied.
        injectStyleSheet: false,
      },
      series: [{ name: label, data: rows.map((row) => countOf(row)) }],
      xaxis: {
        categories: rows.map((row) => String(row.started_at ?? '').slice(5, 10)),
        labels: { style: { colors: colours.text } },
        axisBorder: { color: colours.line },
        axisTicks: { color: colours.line },
      },
      yaxis: { labels: { style: { colors: colours.text } } },
      grid: { borderColor: colours.line },
      colors: [colours.accent],
      dataLabels: { enabled: false },
      states: { hover: { filter: { type: 'none' } } },
    }).render();
  }).catch(() => {
    // A soft dependency, like the map and the tracker: an unreachable chart
    // leaves an empty box and the numbers above it are still the answer.
  }));

  return host;
}

// Calls back once, as soon as the element has a width. Nothing to disconnect
// later: the observer stops itself, and a panel that is replaced before it ever
// gets a width simply never fires.
function whenSized(element, run) {
  if (element.offsetWidth > 0) { run(); return; }
  if (typeof ResizeObserver !== 'function') { run(); return; }

  const observer = new ResizeObserver(() => {
    if (element.offsetWidth <= 0) return;
    observer.disconnect();
    run();
  });
  observer.observe(element);
}

function countOf(row) {
  try {
    return JSON.parse(row.counts_json ?? '{}').staged ?? 0;
  } catch {
    return 0;
  }
}
